import { TableSegmentBreakdownCell } from '@/features/shared/components/tableSystem/TableSegmentBreakdownCell';
import { TableStatusBreakdownCell } from '@/features/shared/components/tableSystem/TableStatusBreakdownCell';
import { resolveEntityTags } from '@/features/shared/tags';
import { releaseStatusOptions } from '@/shared/constants/statusOptions';
import { uiTokens } from '@/theme/uiTokens';
import { formatStatusLabel, getStatusTextColor, Text } from '@/ui';
import {
  getSingleSelectFilterOperators,
  getStringFilterOperators,
} from '@/ui/composites/grid/gridOperators';
import { TagsInlineCell, TableTextCell } from '@/ui/composites/table';
import { formatDate } from '@/ui/formatters/formatDate';
import type { GridColDef, GridRenderCellParams } from '@/ui/primitives';
import type { ReleaseListItemDto } from '@flowqa/shared';

type ReleasesColumnsOptions = {
  onProjectSelect: (projectId: string) => void;
};

const columnFlex = {
  name: 1,
  status: 0.5,
  testingStatus: 0.5,
  features: 1,
  featureHealth: 0.5,
  suites: 1,
  tests: 1,
  tags: 0.75,
};

type StatusSegment = { label: string; value: number; color: string };

export const buildReleasesColumns = ({
  onProjectSelect,
}: ReleasesColumnsOptions): Array<GridColDef<ReleaseListItemDto>> => {
  const stringOperators = getStringFilterOperators();
  const singleSelectOperators = getSingleSelectFilterOperators();
  const nameColumn: GridColDef<ReleaseListItemDto> = {
    field: 'name',
    headerName: 'Release',
    flex: columnFlex.name,
    filterOperators: stringOperators,
    renderCell: (params) => (
      <TableTextCell
        primary={{
          label: params.row.name,
          href: `/releases/${params.row.slug || params.row.id}`,
          onClick: () => {
            onProjectSelect(params.row.projectId);
          },
        }}
      />
    ),
  };

  const statusColumn: GridColDef<ReleaseListItemDto> = {
    field: 'status',
    headerName: 'Status',
    flex: columnFlex.status,
    type: 'singleSelect',
    align: 'center',
    headerAlign: 'center',
    valueOptions: releaseStatusOptions,
    filterOperators: singleSelectOperators,
    renderCell: (params) => {
      const status = params.row.status;
      const label = formatStatusLabel(status);
      return (
        <Text
          role="body"
          component="span"
          sx={{
            color: (theme) => getStatusTextColor(theme, status),
          }}
          transform="uppercase"
        >
          {label.length > 0 ? label : '-'}
        </Text>
      );
    },
  };

  const plannedForColumn: GridColDef<ReleaseListItemDto> = {
    field: 'plannedFor',
    headerName: 'Planned For',
    flex: 1,
    filterable: false,
    sortable: false,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => (
      <Text role="body" component="span">
        {formatDate(params.row.plannedFor) || '-'}
      </Text>
    ),
  };

  const releasedAtColumn: GridColDef<ReleaseListItemDto> = {
    field: 'releasedAt',
    headerName: 'Released At',
    flex: 1,
    filterable: false,
    sortable: false,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => (
      <Text role="body" component="span">
        {formatDate(params.row.releasedAt) || '-'}
      </Text>
    ),
  };

  const featuresColumn: GridColDef<ReleaseListItemDto> = {
    field: 'features',
    headerName: 'Features',
    flex: columnFlex.features,
    align: 'center',
    headerAlign: 'center',
    filterable: false,
    sortable: false,
    renderCell: (params: GridRenderCellParams<ReleaseListItemDto>) => {
      const summaries = params.row.features?.items ?? [];
      const counts = summaries.reduce<Record<'draft' | 'planned' | 'ready' | 'released', number>>(
        (acc, item) => {
          acc[item.status] += 1;
          return acc;
        },
        { draft: 0, planned: 0, ready: 0, released: 0 },
      );
      const total = counts.draft + counts.planned + counts.ready + counts.released;
      const segments: StatusSegment[] = [
        { label: 'Draft', value: counts.draft, color: uiTokens.statusColors.neutral },
        { label: 'Planned', value: counts.planned, color: uiTokens.statusColors.blue },
        { label: 'Ready', value: counts.ready, color: uiTokens.statusColors.orange },
        { label: 'Released', value: counts.released, color: uiTokens.statusColors.green },
      ];
      return (
        <TableSegmentBreakdownCell
          totalLabel={`${total} feature${total === 1 ? '' : 's'}`}
          rows={segments.map((segment) => ({
            key: segment.label,
            label: `${segment.value.toLocaleString()} ${segment.label.toLowerCase()}`,
            count: segment.value,
            color: segment.color,
          }))}
        />
      );
    },
  };

  const suitesColumn: GridColDef<ReleaseListItemDto> = {
    field: 'latestSuiteResults',
    headerName: 'Latest Suites',
    flex: columnFlex.suites,
    align: 'center',
    headerAlign: 'center',
    filterable: false,
    sortable: false,
    renderCell: (params) => {
      const counts = params.row.latestSuiteResults?.counts;
      const executionCounts = {
        passed: counts?.passed,
        failed: counts?.failed,
        errored: counts?.errored,
        skipped: counts?.skipped,
        untested: counts?.untested,
      };
      return (
        <TableStatusBreakdownCell
          entity="suite"
          counts={{
            passed: executionCounts.passed ?? 0,
            failed: executionCounts.failed ?? 0,
            errored: executionCounts.errored ?? 0,
            skipped: executionCounts.skipped ?? 0,
            untested: executionCounts.untested ?? 0,
          }}
        />
      );
    },
  };

  const testsColumn: GridColDef<ReleaseListItemDto> = {
    field: 'latestTestResults',
    headerName: 'Latest Tests',
    flex: columnFlex.tests,
    align: 'center',
    headerAlign: 'center',
    filterable: false,
    sortable: false,
    renderCell: (params) => {
      const counts = params.row.latestTestResults?.counts;
      const executionCounts = {
        passed: counts?.passed,
        failed: counts?.failed,
        errored: counts?.errored,
        skipped: counts?.skipped,
        untested: counts?.untested,
      };
      return (
        <TableStatusBreakdownCell
          entity="test"
          counts={{
            passed: executionCounts.passed ?? 0,
            failed: executionCounts.failed ?? 0,
            errored: executionCounts.errored ?? 0,
            skipped: executionCounts.skipped ?? 0,
            untested: executionCounts.untested ?? 0,
          }}
        />
      );
    },
  };

  const tagsColumn: GridColDef<ReleaseListItemDto> = {
    field: 'tags',
    headerName: 'Tags',
    flex: columnFlex.tags,
    filterable: false,
    sortable: false,
    renderCell: (params) => <TagsInlineCell tags={resolveEntityTags(params.row)} />,
  };

  return [
    nameColumn,
    statusColumn,
    plannedForColumn,
    releasedAtColumn,
    featuresColumn,
    suitesColumn,
    testsColumn,
    tagsColumn,
  ];
};
